import React, { useState, useEffect } from 'react';
import axiosinstance from '../../api/axiosinstance';
import { Link } from 'react-router-dom';
import JourneyWorkflow from './JourneyWorkflow';
import TechStackSection from './TechStackSection';
import WhyChooseSection from './WhyChooseSection';
import IncludedCoursesSection from './IncludedCoursesSection';
import ProgramHighlightsSection from './ProgramHighlightsSection';
import SEO from '../common/SEO';
import LiveProjectsSection from './LiveProjectsSection';
import { MonitorIcon, CodeIcon, TargetIcon, UsersIcon, AwardIcon, DollarIcon, TrendingUpIcon } from './programHighlightIcons';
import { FaJava, FaReact, FaNodeJs, FaPython, FaFigma, FaAws, FaDocker, FaGitAlt } from 'react-icons/fa';
import { SiSpringboot, SiHibernate, SiExpress, SiMongodb, SiMysql, SiRedux, SiPandas, SiKubernetes, SiJira } from 'react-icons/si';

const INT_LIVE_FEATURES = [
  'Work in real-time projects',
  'Company-like Environment',
  'Agile Methodology',
  'Code Reviews',
  'QA Testing',
  'Client Interaction',
  'Deployment on Server',
];

const INT_LIVE_PROJECTS = [
  { name:'Student Management System',  icon:'🎓' },
  { name:'CRM Application',            icon:'👥' },
  { name:'Hospital Management System', icon:'🏥' },
  { name:'LMS Portal',                 icon:'📚' },
  { name:'E-Commerce Platform',        icon:'🛒' },
  { name:'HRMS Application',           icon:'💼' },
];

const INTERNSHIP_HIGHLIGHTS = [
  { label: 'Industry-Aligned Training',          icon: <MonitorIcon /> },
  { label: 'Work on Real Client Projects',       icon: <CodeIcon /> },
  { label: 'Agile Team Environment',             icon: <TargetIcon /> },
  { label: 'Mentor & Peer Collaboration',        icon: <UsersIcon /> },
  { label: 'Certificate & Portfolio',            icon: <AwardIcon /> },
  { label: 'Stipend Opportunity (if available)', icon: <DollarIcon /> },
  { label: 'Career Growth & Full-time Roles',    icon: <TrendingUpIcon /> },
]

const JOURNEY_STEPS = [
  { label: 'Student Enrollment',     icon: 'enroll',      color: '#2563EB' },
  { label: 'Technical Training',     icon: 'training',    color: '#2563EB' },
  { label: 'Assessments',            icon: 'assessment',  color: '#2563EB' },
  { label: 'Mini Project',           icon: 'project',     color: '#2563EB' },
  { label: 'Capstone Project',       icon: 'capstone',    color: '#2563EB' },
  { label: 'Live Project Internship',icon: 'team',        color: '#2563EB' },
  { label: 'Project Review',         icon: 'review',      color: '#2563EB' },
  { label: 'Certification',          icon: 'certificate', color: '#2563EB' },
  { label: 'Placement Support',      icon: 'placement',   color: '#2563EB' },
];

const TRACKS = [
  {
    color: '#2563EB',
    bg: '#EFF6FF',
    border: '#BFDBFE',
    title: 'Full Stack Development',
    duration: '3 Months',
    skills: ['React.js', 'Node.js', 'MongoDB', 'REST APIs', 'Git & GitHub'],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
  },
  {
    color: '#16A34A',
    bg: '#F0FDF4',
    border: '#BBF7D0',
    title: 'Java Full Stack',
    duration: '3 Months',
    skills: ['Core Java', 'Spring Boot', 'Hibernate', 'MySQL', 'REST APIs'],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>,
  },
  {
    color: '#7C3AED',
    bg: '#F5F3FF',
    border: '#DDD6FE',
    title: 'MERN Stack',
    duration: '3 Months',
    skills: ['MongoDB', 'Express.js', 'React.js', 'Node.js', 'Redux'],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>,
  },
  {
    color: '#EA580C',
    bg: '#FFF7ED',
    border: '#FED7AA',
    title: 'Python & Data Science',
    duration: '3 Months',
    skills: ['Python', 'Pandas', 'NumPy', 'Machine Learning', 'Data Visualization'],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#EA580C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>,
  },
  {
    color: '#0891B2',
    bg: '#ECFEFF',
    border: '#A5F3FC',
    title: 'UI/UX Design',
    duration: '2 Months',
    skills: ['Figma', 'Wireframing', 'Prototyping', 'User Research', 'Design Systems'],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0891B2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="21.17" y1="8" x2="12" y2="8"/><line x1="3.95" y1="6.06" x2="8.54" y2="14"/><line x1="10.88" y1="21.94" x2="15.46" y2="14"/></svg>,
  },
  {
    color: '#DC2626',
    bg: '#FEF2F2',
    border: '#FECACA',
    title: 'DevOps & Cloud',
    duration: '2 Months',
    skills: ['AWS', 'Docker', 'Kubernetes', 'CI/CD Pipelines', 'Linux'],
    icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/></svg>,
  },
];

const BENEFITS = [
  { title: 'Real Client Projects', desc: 'Work on live projects for actual clients — not dummy assignments. Build a portfolio that gets noticed.' },
  { title: 'Expert Mentorship', desc: 'One-on-one guidance from industry professionals with 10+ years of hands-on experience.' },
  { title: 'Stipend Opportunity', desc: 'Top performers become eligible for a monthly stipend as part of the Developer Full Time Program.' },
  { title: 'Internship Certificate', desc: 'A recognised certificate from UPL Snipe, valued by 250+ hiring partners across India.' },
  { title: 'Placement Support', desc: 'Direct referrals, interview prep, and active follow-up until you land a full-time role.' },
  { title: 'Agile Work Environment', desc: 'Experience scrum meetings, sprint planning, and code reviews — exactly like a real tech company.' },
];

const INTERNSHIP_DURATIONS = [
  {
    title: '3 Months Internship',
    color: '#16A34A',
    colorDark: '#15803D',
    bg: '#F0FDF4',
    phases: [
      { name: 'Technical Training', duration: '1 Month', items: ['Core Java', 'Database', 'Frontend', 'Hands-on Labs'] },
      { name: 'Mini Project', duration: '1 Month', items: ['Requirement Analysis', 'Coding', 'Testing'] },
      { name: 'Internship Project', duration: '1 Month', items: ['Team Work', 'Deployment', 'Presentation'] },
    ],
    deliverables: ['Internship Certificate', 'Project Certificate', 'GitHub Portfolio'],
  },
  {
    title: '6 Months Internship',
    color: '#7C3AED',
    colorDark: '#6D28D9',
    bg: '#FAF5FF',
    phases: [
      { name: 'Training', duration: '2 Months', items: ['Core Java', 'Spring Boot', 'MySQL', 'Frontend'] },
      { name: 'Capstone Project', duration: '1 Month', items: ['Development', 'Testing', 'Documentation', 'Deployment'] },
      { name: 'Live Project Internship', duration: '3 Months', items: ['Daily Stand-ups', 'Sprint Planning', 'Jira Tasks', 'Client Demonstration'] },
    ],
    deliverables: ['Experience Letter', 'Live Project Certificate', 'Completion Certificate', 'Industry Portfolio'],
  },
];

const INTERNSHIP_TECHS = [
  { name: 'Core Java',    color: '#E76F00', bg: '#FFF7ED', abbr: '☕', icon: FaJava },
  { name: 'Spring Boot',  color: '#6DB33F', bg: '#F0FDF4', abbr: '🌿', icon: SiSpringboot },
  { name: 'Hibernate',    color: '#BCA136', bg: '#FEFCE8', abbr: 'H', icon: SiHibernate },
  { name: 'React.js',     color: '#61DAFB', bg: '#F0F9FF', abbr: '⚛', icon: FaReact },
  { name: 'Node.js',      color: '#339933', bg: '#F0FDF4', abbr: 'N', icon: FaNodeJs },
  { name: 'Express.js',   color: '#000000', bg: '#F8FAFC', abbr: 'Ex', icon: SiExpress },
  { name: 'MongoDB',      color: '#47A248', bg: '#F0FDF4', abbr: 'M', icon: SiMongodb },
  { name: 'MySQL',        color: '#4479A1', bg: '#F0F9FF', abbr: 'SQL', icon: SiMysql },
  { name: 'Redux',        color: '#764ABC', bg: '#FAF5FF', abbr: 'Rx', icon: SiRedux },
  { name: 'Python',       color: '#3776AB', bg: '#EFF6FF', abbr: '🐍', icon: FaPython },
  { name: 'Pandas/NumPy', color: '#150458', bg: '#FAF5FF', abbr: 'Pd', icon: SiPandas },
  { name: 'Figma',        color: '#F24E1E', bg: '#FDF2F8', abbr: 'Fig', icon: FaFigma },
  { name: 'AWS',          color: '#FF9900', bg: '#FFFBEB', abbr: 'aws', icon: FaAws },
  { name: 'Docker',       color: '#2496ED', bg: '#EFF6FF', abbr: '🐳', icon: FaDocker },
  { name: 'Kubernetes',   color: '#326CE5', bg: '#EFF6FF', abbr: 'K8s', icon: SiKubernetes },
  { name: 'Git & GitHub', color: '#F05032', bg: '#FFF7ED', abbr: '⎇', icon: FaGitAlt },
  { name: 'Jira',         color: '#0052CC', bg: '#EFF6FF', abbr: 'Jira', icon: SiJira },
  { name: 'and more...',  color: '#6B7280', bg: '#F9FAFB', abbr: '...' },
];

const INTERNSHIP_WHY_CHOOSE = [
  { title: 'Real Client Projects',      sub: 'Work on live industry projects',     icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg> },
  { title: 'Mentorship from Experts',   sub: 'Guided by industry professionals',   icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/></svg> },
  { title: 'Agile Team Experience',     sub: 'Daily stand-ups & sprint cycles',    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 118 2.83"/><path d="M22 12A10 10 0 0012 2v10z"/></svg> },
  { title: 'Certificate & Portfolio',   sub: 'Showcase your real work',           icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg> },
  { title: 'Hands-on Learning',         sub: 'Build by doing, not just theory',   icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg> },
  { title: 'Career Growth',             sub: 'Path to full-time placement',       icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg> },
];

const FAQS = [
  { q: 'Who can apply for the internship?', a: 'Students in their final year, recent graduates, or anyone who has completed a technical course and wants real-world project experience.' },
  { q: 'Is the internship paid?', a: 'Top performers during the internship are eligible for a stipend through our Developer Full Time Program. Regular internship tracks are learning-focused.' },
  { q: 'How long is the internship?', a: 'Duration ranges from 2 to 3 months depending on the track selected. Flexible schedules are available for students still in college.' },
  { q: 'Will I work on real projects?', a: 'Yes. Every intern works on live client projects from day one — following real Agile sprints, standups, and code review processes.' },
  { q: 'What happens after the internship?', a: 'You receive a completion certificate and get priority access to UPL\'s placement network for full-time job placements.' },
];

const InternshipPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    axiosinstance.get('/api/category/all/3')
      .then(res => setCourses(res.data.filter(c => c.pdf_file_path)))
      .catch(() => {})
  }, []);

  return (
    <>
      <SEO
        title="Internship Program"
        path="/programs/internship"
        description="UPL Snipe's Internship Program — 3 to 6 month real-world internship with live client projects, agile practices, and career guidance. Launch your tech career with hands-on experience."
        keywords="internship program, tech internship, live project internship, IT internship India, software internship, career launch program"
      />
      <style>{`
        #int-page { font-family:'Inter','Segoe UI',sans-serif; }
        #int-hero { background:linear-gradient(135deg,#050D1E 0%,#0A1628 45%,#0C2040 100%) !important; padding:72px 5% 64px !important; min-height:100vh !important; box-sizing:border-box !important; }
        #int-hero-inner { max-width:1100px; margin:0 auto; display:grid; grid-template-columns:1fr 400px; gap:56px; align-items:center; }
        #int-eyebrow { display:inline-block; font-size:11px; font-weight:800; letter-spacing:0.14em; color:rgba(255,255,255,0.65); background:rgba(59,130,246,0.15); border:1px solid rgba(59,130,246,0.3); padding:4px 14px; border-radius:20px; margin-bottom:16px; text-transform:uppercase; }
        #int-title { font-size:clamp(28px,4vw,50px); font-weight:900; color:#fff; line-height:1.15; letter-spacing:-0.03em; margin:0 0 10px; }
        #int-title span { background:linear-gradient(90deg,#3B82F6,#60A5FA); -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
        #int-tagline { font-size:14px; color:#60A5FA; font-weight:600; margin:0 0 14px; }
        #int-sub { font-size:15px; color:rgba(255,255,255,0.72); line-height:1.75; margin:0 0 28px; }
        #int-hero-btns { display:flex; gap:12px; flex-wrap:wrap; }
        .int-btn-primary { padding:12px 28px; background:#2563EB; color:#fff; text-decoration:none; border-radius:10px; font-size:14px; font-weight:700; border:none; cursor:pointer; transition:background 0.2s; }
        .int-btn-primary:hover { background:#1D4ED8; color:#fff; }
        .int-btn-outline { padding:12px 28px; background:transparent; border:1.5px solid rgba(255,255,255,0.25); color:#fff; text-decoration:none; border-radius:10px; font-size:14px; font-weight:600; cursor:pointer; transition:all 0.2s; }
        .int-btn-outline:hover { border-color:#60A5FA; color:#60A5FA; }
        #int-hero-card { background:rgba(255,255,255,0.06); border:1px solid rgba(59,130,246,0.25); border-radius:16px; padding:28px; }
        #int-hero-card h3 { font-size:13px; font-weight:800; color:#60A5FA; margin:0 0 16px; letter-spacing:0.06em; text-transform:uppercase; }
        .int-check { display:flex; align-items:flex-start; gap:10px; margin-bottom:11px; font-size:13.5px; color:rgba(255,255,255,0.85); line-height:1.5; }
        .int-check-dot { width:20px; height:20px; border-radius:50%; background:rgba(59,130,246,0.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:1px; }
        #int-stats { background:#0B1629 !important; padding:36px 5% !important; }
        #int-stats-inner { max-width:1100px; margin:0 auto; display:grid; grid-template-columns:repeat(4,1fr); }
        .int-stat { text-align:center; padding:12px; border-right:1px solid rgba(59,130,246,0.15); }
        .int-stat:last-child { border-right:none; }
        .int-stat-val { font-size:32px; font-weight:900; color:#3B82F6; line-height:1; margin-bottom:5px; }
        .int-stat-lbl { font-size:12px; color:rgba(255,255,255,0.5); font-weight:500; }
        #int-tracks { background:#F8FAFC; padding:72px 5%; }
        #int-tracks-inner { max-width:1100px; margin:0 auto; }
        .int-eyebrow { display:inline-block; font-size:11px; font-weight:800; letter-spacing:0.14em; color:#2563EB; background:#EFF6FF; border:1px solid #BFDBFE; padding:4px 14px; border-radius:20px; margin-bottom:14px; text-transform:uppercase; }
        .int-section-title { font-size:clamp(22px,3vw,34px); font-weight:900; color:#0F172A; line-height:1.2; letter-spacing:-0.02em; margin:0 0 8px; }
        .int-section-title span { color:#2563EB; }
        .int-section-sub { font-size:14.5px; color:#64748B; margin:0 0 36px; }
        #int-tracks-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
        .int-track-card { background:#fff; border-radius:14px; padding:24px; border:1.5px solid #E2E8F0; transition:box-shadow 0.2s,border-color 0.2s; }
        .int-track-card:hover { box-shadow:0 8px 32px rgba(0,0,0,0.08); }
        .int-track-icon { width:48px; height:48px; border-radius:12px; display:flex; align-items:center; justify-content:center; margin-bottom:14px; }
        .int-track-title { font-size:15px; font-weight:800; color:#0F172A; margin:0 0 4px; }
        .int-track-dur { font-size:12px; font-weight:600; margin:0 0 14px; }
        .int-track-skill { display:inline-block; font-size:11px; font-weight:600; padding:3px 10px; border-radius:20px; margin:0 4px 6px 0; }
        #int-benefits { background:#F8FAFC; padding:72px 5%; border-top:1px solid #E2E8F0; }
        #int-benefits-inner { max-width:1100px; margin:0 auto; }
        #int-benefits-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; margin-top:40px; }
        .int-ben-card { background:#fff; border:1.5px solid #E2E8F0; border-radius:14px; padding:22px; }
        .int-ben-title { font-size:15px; font-weight:800; color:#0F172A; margin:0 0 8px; display:flex; align-items:center; gap:8px; }
        .int-ben-desc { font-size:13px; color:#64748B; line-height:1.65; margin:0; }
        #int-faq { background:#fff; padding:72px 5%; border-top:1px solid #E2E8F0; }
        #int-faq-inner { max-width:760px; margin:0 auto; }
        .int-faq-item { border:1.5px solid #E2E8F0; border-radius:12px; margin-bottom:12px; overflow:hidden; }
        .int-faq-q { display:flex; justify-content:space-between; align-items:center; padding:16px 20px; cursor:pointer; font-size:14px; font-weight:700; color:#0F172A; gap:12px; }
        .int-faq-q:hover { background:#F8FAFC; }
        .int-faq-a { padding:0 20px 16px; font-size:13.5px; color:#475569; line-height:1.7; }
        #int-cta { background:linear-gradient(135deg,#050D1E 0%,#0A1628 100%) !important; padding:64px 5% !important; text-align:center; border-top:1px solid rgba(59,130,246,0.15); }
        #int-cta-inner { max-width:700px; margin:0 auto; }
        #int-cta h2 { font-size:clamp(22px,3vw,36px); font-weight:900; color:#fff; margin:0 0 14px; letter-spacing:-0.02em; }
        #int-cta p { font-size:15px; color:rgba(255,255,255,0.65); margin:0 0 28px; line-height:1.7; }
        #int-cta-btns { display:flex; gap:12px; justify-content:center; flex-wrap:wrap; }
        #int-why-choose { background:#0B1629 !important; }
        #int-structure { background:linear-gradient(180deg,#F8FAFC 0%,#F1F5F9 100%); padding:80px 5%; border-top:1px solid #E2E8F0; }
        #int-structure-inner { max-width:1200px; margin:0 auto; }
        #int-structure-grid { display:grid; grid-template-columns:1fr 1fr; gap:28px; margin-top:44px; }
        .int-dur-card { background:#fff; border-radius:20px; border:1px solid #E2E8F0; overflow:hidden; box-shadow:0 1px 3px rgba(0,0,0,0.04); transition:box-shadow 0.25s,transform 0.25s; }
        .int-dur-card:hover { box-shadow:0 16px 40px rgba(0,0,0,0.08); transform:translateY(-3px); }
        .int-dur-header { display:flex; align-items:center; justify-content:space-between; gap:10px; color:#fff; font-weight:800; font-size:17px; padding:18px 24px; }
        .int-dur-header-left { display:flex; align-items:center; gap:12px; }
        .int-dur-header-icon { width:38px; height:38px; border-radius:10px; background:rgba(255,255,255,0.18); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .int-dur-header-badge { font-size:11.5px; font-weight:700; background:rgba(255,255,255,0.18); padding:5px 12px; border-radius:999px; white-space:nowrap; }
        .int-dur-body { padding:26px 24px 22px; }
        .int-dur-phases { display:flex; align-items:stretch; gap:0; margin-bottom:22px; }
        .int-dur-phase { flex:1; min-width:0; padding:0 14px; }
        .int-dur-phase:not(:first-child) { border-left:1.5px dashed #E2E8F0; }
        .int-dur-phase-badge { width:26px; height:26px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:800; color:#fff; margin:0 auto 10px; }
        .int-dur-phase-name { font-size:13.5px; font-weight:800; color:#0F172A; text-align:center; margin-bottom:2px; line-height:1.3; min-height:35px; display:flex; align-items:center; justify-content:center; }
        .int-dur-phase-dur { font-size:11px; color:#94A3B8; font-weight:700; text-align:center; text-transform:uppercase; letter-spacing:0.04em; margin-bottom:12px; }
        .int-dur-phase-list { list-style:none; padding:0; margin:0; font-size:12px; color:#475569; line-height:2; }
        .int-dur-phase-list li { display:block; width:100%; padding-left:14px; position:relative; }
        .int-dur-phase-list li::before { content:''; position:absolute; left:0; top:9px; width:6px; height:6px; border-radius:50%; background:var(--accent); opacity:0.55; }
        .int-dur-footer { display:flex; gap:10px; flex-wrap:wrap; border-top:1px solid #F1F5F9; padding-top:18px; }
        .int-dur-deliverable { display:flex; align-items:center; gap:6px; font-size:12px; font-weight:700; padding:7px 14px; border-radius:999px; }
        @media(max-width:900px) { #int-hero { min-height:auto !important; } #int-hero-inner { grid-template-columns:1fr; } #int-hero-card { display:none; } #int-tracks-grid,#int-benefits-grid { grid-template-columns:1fr 1fr; } #int-stats-inner { grid-template-columns:repeat(2,1fr); } #int-structure-grid { grid-template-columns:1fr; } }
        @media(max-width:560px) {
          #int-tracks-grid,#int-benefits-grid { grid-template-columns:1fr; }
          .int-dur-phases { flex-direction:column; gap:18px; }
          .int-dur-phase:not(:first-child) { border-left:none; border-top:1.5px dashed #E2E8F0; padding-top:14px; }
          .int-dur-phase-name { min-height:auto; text-align:left !important; justify-content:flex-start !important; }
          .int-dur-phase-dur { text-align:left !important; }
          .int-dur-phase-badge { margin:0 0 10px !important; }
        }
      `}</style>

      <div id="int-page">

        {/* HERO */}
        <section id="int-hero">
          <div id="int-hero-inner">
            <div>
              <div id="int-eyebrow">UPL Program</div>
              <h1 id="int-title">Launch Your Career with a<br /><span>Real Internship</span></h1>
              <div style={{ display:'inline-flex', alignItems:'center', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.16)', borderRadius:999, padding:'7px 20px', margin:'12px 0 14px' }}>
                {['3 Months','6 Months','Offline','Online','Hybrid'].map((t,i,a) => (
                  <React.Fragment key={t}>
                    <span style={{ fontSize:13, fontWeight:700, color:'#fff' }}>{t}</span>
                    {i<a.length-1 && <span style={{ color:'rgba(255,255,255,0.35)', margin:'0 10px' }}>|</span>}
                  </React.Fragment>
                ))}
              </div>
              <p id="int-tagline">Work on Live Projects. Build Real Skills. Get Hired.</p>
              <p id="int-sub">
                UPL's Internship Program immerses you in client projects from day one. You'll collaborate with seasoned mentors, adopt industry standard Agile practices, and build a portfolio that captures the attention of hiring managers.
              </p>
              <div id="int-hero-btns">
                <button className="int-btn-primary" onClick={() => window.dispatchEvent(new CustomEvent('openApplyModal', { detail: { program: 'Internship' } }))}>
                  Apply for Internship
                </button>
                <button className="int-btn-outline" onClick={() => document.getElementById('courses-section')?.scrollIntoView({ behavior:'smooth' })}>
                  View Courses
                </button>
              </div>
            </div>

            <div id="int-hero-card">
              <h3>What You Will Experience</h3>
              {[
                'Work on live client projects from day one',
                'Follow real Agile sprints & standups',
                'Get code reviewed by senior developers',
                'Build a portfolio with actual deliverables',
                'Earn an internship completion certificate',
                'Get placed via UPL\'s hiring network',
              ].map((item, i) => (
                <div className="int-check" key={i}>
                  <div className="int-check-dot">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* STATS */}
        <section id="int-stats">
          <div id="int-stats-inner">
            {[
              { val: '6', lbl: 'Internship Tracks' },
              { val: '150+', lbl: 'Live Projects' },
              { val: '250+', lbl: 'Hiring Partners' },
              { val: '2–3', lbl: 'Months Duration' },
            ].map(s => (
              <div className="int-stat" key={s.lbl}>
                <div className="int-stat-val">{s.val}</div>
                <div className="int-stat-lbl">{s.lbl}</div>
              </div>
            ))}
          </div>
        </section>

        {/* PROGRAM HIGHLIGHTS */}
        <ProgramHighlightsSection
          items={INTERNSHIP_HIGHLIGHTS}
          accentColor="#2563EB"
          iconBg="#EFF6FF"
          iconBorder="#BFDBFE"
          cardBorder="#DBEAFE"
        />

        {/* COURSES */}
        <div id="courses-section">
          <IncludedCoursesSection courses={courses} programId={3} programName="Internship Program" bg="#F5F3FF" />
        </div>

        {/* PROGRAM STRUCTURE */}
        <section id="int-structure">
          <div id="int-structure-inner">
            <div style={{ textAlign: 'center' }}>
              <div className="int-eyebrow" style={{ margin: '0 auto 14px' }}>Program Structure</div>
              <h2 className="int-section-title">Internship <span>Duration Options</span></h2>
              <p className="int-section-sub">Choose between a focused 3-month internship or an extended 6-month program with a live client project.</p>
            </div>
            <div id="int-structure-grid">
              {INTERNSHIP_DURATIONS.map(d => (
                <div className="int-dur-card" key={d.title} style={{ '--accent': d.color }}>
                  <div className="int-dur-header" style={{ background: `linear-gradient(135deg, ${d.color}, ${d.colorDark})` }}>
                    <div className="int-dur-header-left">
                      <div className="int-dur-header-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      </div>
                      {d.title}
                    </div>
                    <div className="int-dur-header-badge">{d.phases.length} Phases</div>
                  </div>
                  <div className="int-dur-body">
                    <div className="int-dur-phases">
                      {d.phases.map((p, i) => (
                        <div className="int-dur-phase" key={p.name}>
                          <div className="int-dur-phase-badge" style={{ background: d.color }}>{i + 1}</div>
                          <div className="int-dur-phase-name">{p.name}</div>
                          <div className="int-dur-phase-dur">{p.duration}</div>
                          <ul className="int-dur-phase-list">
                            {p.items.map(it => <li key={it}>{it}</li>)}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="int-dur-footer">
                      {d.deliverables.map(del => (
                        <div className="int-dur-deliverable" key={del} style={{ background: d.bg, color: d.color }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                          {del}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TRACKS */}
        <section id="int-tracks">
          <div id="int-tracks-inner">
            <div style={{ textAlign: 'center' }}>
              <div className="int-eyebrow" style={{ margin: '0 auto 14px' }}>Specialisations</div>
              <h2 className="int-section-title">Choose Your <span>Internship Track</span></h2>
              <p className="int-section-sub">Pick the domain that aligns with your career goal. All tracks include live projects and placement support.</p>
            </div>
            <div id="int-tracks-grid">
              {TRACKS.map(t => (
                <div className="int-track-card" key={t.title} style={{ borderColor: t.border }}>
                  <div className="int-track-icon" style={{ background: t.bg }}>{t.icon}</div>
                  <div className="int-track-title">{t.title}</div>
                  <div className="int-track-dur" style={{ color: t.color }}>{t.duration}</div>
                  <div>
                    {t.skills.map(s => (
                      <span key={s} className="int-track-skill" style={{ background: t.bg, color: t.color }}>{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* JOURNEY WORKFLOW */}
        <JourneyWorkflow title="Internship Journey Workflow" steps={JOURNEY_STEPS} accentColor="#2563EB" />

        {/* TECHNOLOGY YOU WILL LEARN */}
        <TechStackSection title="Technology You Will Learn" accentColor="#2563EB" bg="#F5F3FF" border="#E2E8F0" techs={INTERNSHIP_TECHS} />

        {/* LIVE PROJECTS */}
        <LiveProjectsSection
          accentColor="#2563EB"
          accentBg="#EFF6FF"
          accentBorder="#BFDBFE"
          title="Live Project Internship Experience"
          features={INT_LIVE_FEATURES}
          projects={INT_LIVE_PROJECTS}
        />

        {/* WHY CHOOSE OUR INTERNSHIP PROGRAM */}
        <WhyChooseSection
          id="int-why-choose"
          title="WHY CHOOSE OUR INTERNSHIP PROGRAM?"
          bg="#0B1629"
          iconColor="#60A5FA"
          iconBg="rgba(37,99,235,0.12)"
          iconBorder="rgba(96,165,250,0.25)"
          items={INTERNSHIP_WHY_CHOOSE}
        />

        {/* FAQ */}
        <section id="int-faq">
          <div id="int-faq-inner">
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <div className="int-eyebrow" style={{ margin: '0 auto 14px' }}>FAQ</div>
              <h2 className="int-section-title">Common <span>Questions</span></h2>
            </div>
            {FAQS.map((f, i) => (
              <div className="int-faq-item" key={i}>
                <div className="int-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {f.q}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </div>
                {openFaq === i && <div className="int-faq-a">{f.a}</div>}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section id="int-cta">
          <div id="int-cta-inner">
            <div className="int-eyebrow" style={{ background: 'rgba(59,130,246,0.15)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.3)', margin: '0 auto 16px' }}>Apply Now</div>
            <h2>Start Your Internship Journey Today</h2>
            <p>Real projects. Real experience. Real career growth. Apply for UPL's Internship Program and take the first step towards your tech career.</p>
            <div id="int-cta-btns">
              <button className="int-btn-primary" style={{ padding: '13px 32px', fontSize: 15 }}
                onClick={() => window.dispatchEvent(new CustomEvent('openApplyModal', { detail: { program: 'Internship' } }))}>
                Apply for Internship
              </button>
              <button className="int-btn-outline" onClick={() => document.getElementById('courses-section')?.scrollIntoView({ behavior:'smooth' })}>View Courses</button>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default InternshipPage;
